import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db, storage } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual, getUsuarioAtualComTenant } from "../Utils/tenant";

const estadoInicial = { cliente: "", clienteId: "", obra: "", obraId: "", numeroCertificado: "", descricaoEquipamento: "", dataCalibracao: "", dataVencimento: "", arquivoCertificado: null };
const nomeSeguro = (nome) => String(nome || "certificado.pdf").replace(/[^a-zA-Z0-9._-]/g, "-");

export default function CalibracaoExternaPrensa() {
  const { id } = useParams();
  const history = useHistory();
  const [form, setForm] = useState(estadoInicial);
  const [arquivo, setArquivo] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const editando = id && id !== "novo";

  async function carregarObras(clienteId) {
    if (!clienteId) { setObras([]); return; }
    const snap = await getDocs(consultaTenantAtual(query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc"))));
    setObras(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  useEffect(() => {
    getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome", "asc")))).then((snap) => setClientes(snap.docs.map((item) => ({ id: item.id, ...item.data() }))));
  }, []);

  useEffect(() => {
    if (!editando) return;
    getDoc(doc(db, "tblCalibracoesExternasPrensa", id)).then((snap) => {
      if (snap.exists()) {
        const dados = { ...estadoInicial, ...snap.data() };
        setForm(dados);
        carregarObras(dados.clienteId);
      }
    });
  }, [editando, id]);

  async function salvar() {
    if (!form.clienteId || !form.obraId || !form.numeroCertificado.trim() || !form.descricaoEquipamento.trim() || !form.dataCalibracao || !form.dataVencimento) {
      alert("Preencha cliente, obra, número do certificado, descrição do equipamento e as datas.");
      return;
    }
    if (!arquivo && !form.arquivoCertificado?.storagePath) {
      alert("Anexe o PDF do certificado.");
      return;
    }
    if (arquivo && arquivo.type !== "application/pdf") {
      alert("Selecione um arquivo PDF.");
      return;
    }
    try {
      setSalvando(true);
      const dados = { cliente: form.cliente, clienteId: form.clienteId, obra: form.obra, obraId: form.obraId, numeroCertificado: form.numeroCertificado.trim(), descricaoEquipamento: form.descricaoEquipamento.trim(), dataCalibracao: form.dataCalibracao, dataVencimento: form.dataVencimento, calibracaoExterna: true, ativo: true, atualizadoEm: serverTimestamp() };
      let documentoId = id;
      if (editando) await updateDoc(doc(db, "tblCalibracoesExternasPrensa", id), dados);
      else {
        const criado = await addDoc(collection(db, "tblCalibracoesExternasPrensa"), aplicarTenantAtual({ ...dados, criadoEm: serverTimestamp() }));
        documentoId = criado.id;
      }
      if (arquivo) {
        const usuario = await getUsuarioAtualComTenant();
        if (!usuario.tenantId) throw new Error("USUARIO_SEM_TENANT_ID");
        const storagePath = `tenants/${usuario.tenantId}/calibracoes-prensa-externas/${documentoId}/${Date.now()}-${nomeSeguro(arquivo.name)}`;
        const destino = ref(storage, storagePath);
        await uploadBytes(destino, arquivo, { contentType: "application/pdf" });
        await updateDoc(doc(db, "tblCalibracoesExternasPrensa", documentoId), { arquivoCertificado: { storagePath, nomeArquivo: arquivo.name, tipo: "application/pdf", url: await getDownloadURL(destino) } });
      }
      history.push("/app/calibracao-prensa-externa");
    } catch (erro) {
      console.error("Erro ao salvar calibração externa:", erro);
      alert("Não foi possível salvar a calibração externa.");
    } finally { setSalvando(false); }
  }

  return <><Navbar /><Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}><Paper sx={{ p: 3 }}>
    <Typography variant="h5" sx={{ mb: 3 }}>{editando ? "Editar" : "Nova"} calibração externa de prensa</Typography>
    <Stack spacing={2}>
      <Autocomplete required options={clientes} value={clientes.find((item) => item.id === form.clienteId) || null} getOptionLabel={(item) => item?.nome || ""} isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_, cliente) => { setForm({ ...form, cliente: cliente?.nome || "", clienteId: cliente?.id || "", obra: "", obraId: "" }); carregarObras(cliente?.id); }} renderInput={(params) => <TextField {...params} required label="Cliente" />} />
      <Autocomplete required disabled={!form.clienteId} options={obras} value={obras.find((item) => item.id === form.obraId) || null} getOptionLabel={(item) => item?.nome || ""} isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_, obra) => setForm({ ...form, obra: obra?.nome || "", obraId: obra?.id || "" })} renderInput={(params) => <TextField {...params} required label="Obra" />} />
      <TextField required label="Número do certificado" value={form.numeroCertificado} onChange={(e) => setForm({ ...form, numeroCertificado: e.target.value })} />
      <TextField required label="Descrição do equipamento" value={form.descricaoEquipamento} onChange={(e) => setForm({ ...form, descricaoEquipamento: e.target.value })} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}><TextField required fullWidth type="date" label="Data de calibração" InputLabelProps={{ shrink: true }} value={form.dataCalibracao} onChange={(e) => setForm({ ...form, dataCalibracao: e.target.value })} /><TextField required fullWidth type="date" label="Data de vencimento" InputLabelProps={{ shrink: true }} value={form.dataVencimento} onChange={(e) => setForm({ ...form, dataVencimento: e.target.value })} /></Stack>
      <Button component="label" variant="outlined">Anexar certificado em PDF<input hidden type="file" accept="application/pdf" onChange={(e) => setArquivo(e.target.files?.[0] || null)} /></Button>
      <Typography variant="body2">{arquivo?.name || form.arquivoCertificado?.nomeArquivo || "Nenhum arquivo selecionado"}</Typography>
      <Stack direction="row" spacing={2} justifyContent="flex-end"><Button onClick={() => history.goBack()}>Cancelar</Button><Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button></Stack>
    </Stack>
  </Paper></Box></>;
}
